//import { useManualRefreshJWT } from '../../../store/userstore/useJWTStore';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import { TIMEOUT_PERIODS } from '../../../settings';
import { useAuthenticationState } from '../../../store/useAuthenticationState';
import { gql, useMutation } from '@apollo/client';
import { refreshPhpSession } from './refreshSession';
import { isTokenStale } from '@/lib/authToken';

import {WarningControl} from './AutoLogoutComponents/warningControl';
import {LockScreen} from './AutoLogoutComponents/lockScreen';
import {DiscountModal} from '@/pages/users/auth/AutoLogoutComponents/discountModal';

const REFRESH_JWT = gql`
  mutation RefreshJWT {
    refreshJWT
  }
`;

const LOCK_SESSION = gql`
  mutation LockSession {
    lockSession
  }
`;

const TOKEN_STALE_WINDOW_MS = 10 * 60 * 1000;
let sharedRefreshPromise: Promise<boolean> | null = null;




export const AutoLogout = () => {
    const { authenticationState, setAuthenticationStateJWT } = useAuthenticationState();
    const [refreshJWTMutation] = useMutation(REFRESH_JWT);
    const [lockSessionMutation] = useMutation(LOCK_SESSION);

    const [state, setState] = useState('active');
    const stateRef = useRef('active');

    const lastActivityTime = useRef(Date.now());
    const tokenFailed = useRef(false);
    const sessionTimerRef = useRef<NodeJS.Timeout | null>(null);    

    const refreshJWT = useCallback(async () => {
        // Block all attempts if token has already failed
        if (tokenFailed.current) {
            return false;
        }

        // Single-flight guard to prevent duplicate refresh calls.
        if (sharedRefreshPromise) {
            return sharedRefreshPromise;
        }

        sharedRefreshPromise = (async () => {
            try {
                const {data} = await refreshJWTMutation();
                if (!data || !data.refreshJWT) {
                    console.error('Failed to refresh JWT: No token returned');
                    tokenFailed.current = true;
                    // Clear the interval to stop further attempts
                    if (sessionTimerRef.current) {
                        clearInterval(sessionTimerRef.current);
                        sessionTimerRef.current = null;
                    }
                    // Redirect to login
                    window.location.href = '/login';
                    return false;
                }
                const newToken = data.refreshJWT;
                setAuthenticationStateJWT(newToken);
                // Also refresh PHP session to keep it alive
                await refreshPhpSession(newToken);
                return true;
            } catch (error) {
                console.error('Error refreshing JWT:', error);
                tokenFailed.current = true;
                // Clear the interval to stop further attempts
                if (sessionTimerRef.current) {
                    clearInterval(sessionTimerRef.current);
                    sessionTimerRef.current = null;
                }
                // Redirect to login
                window.location.href = '/login';
                return false;
            } finally {
                sharedRefreshPromise = null;
            }
        })();

        return sharedRefreshPromise;
    }, [refreshJWTMutation, setAuthenticationStateJWT])


    const checkUserActivity = useCallback(async ()  => {
        // Don't refresh if token has already failed
        if (tokenFailed.current) return;

        // Don't refresh if user is locked or has warning modal
        if (stateRef.current=='locked') return;
        if (stateRef.current=='pending') return;

        // Only refresh JWT if user has been active recently
        const timeSinceActivity = Date.now() - lastActivityTime.current;
        if (timeSinceActivity <= TIMEOUT_PERIODS.ACTIVITYTIME) {
            if (isTokenStale(authenticationState, TOKEN_STALE_WINDOW_MS)) {
                await refreshJWT();
            }
            return;
        }

        // Show warning if inactive too long
        stateRef.current = 'pending';
        setState('pending');
    }, [authenticationState, refreshJWT])

    const handleActivity = () => {
        lastActivityTime.current = Date.now();        
    }
    
    const reset = () => {
        stateRef.current = 'active';
        setState('active');
        handleActivity();
      };

    const lock = async () => {
        try {
            await lockSessionMutation();
        } catch (e) {
            // Non-fatal: even if locking fails, proceed to lock UI
            console.warn('Failed to lock session on server', e);
        }
        stateRef.current = 'locked';
        setState('locked');
    }

    useEffect(() => {
        sessionTimerRef.current = setInterval(checkUserActivity, TIMEOUT_PERIODS.SESSION);

        // If an existing token is already near expiration, refresh once on mount.
        if (isTokenStale(authenticationState, TOKEN_STALE_WINDOW_MS)) {
            void refreshJWT();
        }

        document.addEventListener('mousemove', handleActivity);
        document.addEventListener('keydown', handleActivity);
        document.addEventListener('click', handleActivity);

        return () => {
            document.removeEventListener('mousemove', handleActivity);
            document.removeEventListener('keydown', handleActivity);
            document.removeEventListener('click', handleActivity);
            if (sessionTimerRef.current) {
                clearInterval(sessionTimerRef.current);
            }
        };
    }, [authenticationState, checkUserActivity, refreshJWT]);


        return (
            <div>
            {(state == 'pending' || state == 'locked') &&
                <DiscountModal>
                    {state=='pending' && <WarningControl onReset={reset} lock={lock}   />}
                    {state=='locked' && <LockScreen onSuccess={reset}   />}
                </DiscountModal>}
            </div>
        )
        
};
